import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import Badge from '@repo/ui/badge'
import DataTable, { Column } from '@repo/ui/data-table'
import { UserFourIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { EnrollmentDisplayVersionEnum } from '../../../../api/enrollments/get-display-version'
import { getEnrollmentSiblings } from '../../../../api/enrollments/get-enrollment-siblings'
import useTranslate from '../../../../lib/hooks/use-translate'
import { genderOneLetterMapping } from '../../../../lib/types/enums/common/gender.enum'
import { languageLevelEnumMapping } from '../../../../lib/types/enums/languages/language-level.enum'
import { maritalStatusEnumMapping } from '../../../../lib/types/enums/staff-application/marital-status.enum'
import {
  enrollmentSiblingBasicFields,
  EnrollmentSiblingBasicType
} from '../../../../lib/types/fields/enrollments/enrollment-sibling'
import { Enrollment } from '../../../../lib/types/models/enrollment/enrollment'
import { HowDidYouObtainLecBrochure } from '../../../../lib/types/models/how-did-you-obtain-lec-brochures/how-did-you-obtain-lec-brochure'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'
import LoadingScreen from '../../../elements/loading/loading-screen'
import { getCharacteristicName } from '../../enrollment-characteristics/utils'
import LabelWithText from '../../ols/label-with-text'
import ClientEnrollmentProfileCard from '../home/client-enrollment-profile-card'

export type EnrollmentChildBasicDataProps = Pick<
  Enrollment,
  | 'id'
  | 'identityPictureLocked'
  | 'childEmail'
  | 'childPhoneNumber'
  | 'chosenCountryLanguageLevel'
  | 'spokenLanguages'
  | 'doesChildSmoke'
  | 'familySituation'
  | 'parentSocialSecurityNumber'
  | 'characteristics'
  | 'hobbies'
  | 'holdingLECBrochure'
  | 'isChildFirstLanguageStay'
  | 'childPreviousTravelOrganization'
> & {
  secureIdentityPicture?: Pick<
    NonNullable<Enrollment['secureIdentityPicture']>,
    'id' | 'uploadId' | 'name' | 'size' | 'caption' | 'type'
  >
  howDidYouObtainLECBrochure?: Pick<HowDidYouObtainLecBrochure, 'id' | 'name'>
}

type Props = {
  enrollmentPicturePath?: string
  childName?: string
  enrollment: EnrollmentChildBasicDataProps
  refetch: () => void
  displayVersion: EnrollmentDisplayVersionEnum
}

const columns: Column<EnrollmentSiblingBasicType>[] = [
  {
    enableSorting: true,
    header: 'Nom',
    accessorKey: 'name'
  },
  {
    enableSorting: true,
    header: 'Sexe',
    accessorKey: 'gender',
    cell: ({ row }) => genderOneLetterMapping[row.original.gender].label
  },
  {
    enableSorting: true,
    header: 'Âge',
    accessorKey: 'age'
  }
]

const ChildBasicData = ({
  enrollment,
  enrollmentPicturePath,
  childName,
  displayVersion,
  refetch
}: Props) => {
  const { t } = useTranslate()

  const { data: siblings } = useQuery({
    queryKey: ['enrollment-siblings', enrollment.id, enrollmentSiblingBasicFields],
    queryFn: () => getEnrollmentSiblings(enrollment.id, enrollmentSiblingBasicFields),
    enabled: !!enrollment.id
  })

  if (!siblings) return <LoadingScreen />
  return (
    <div className="flex gap-3 items-start w-full mobile:flex-col ">
      <div className="w-full flex flex-col gap-3">
        <InnerBox className="rounded-lg p-4 bg-white-hover flex flex-row gap-3 items-center ">
          <div className="rounded-2xl w-20 h-20 object-cover bg-grey-light-hover flex justify-center items-center">
            {enrollmentPicturePath && enrollment ? (
              <ClientEnrollmentProfileCard
                pictureOnly
                enrollment={enrollment}
                maxImageSizeMB={16}
                disabled={enrollment?.identityPictureLocked}
                refetch={refetch}
              />
            ) : (
              <UserFourIcon
                filled
                className="w-full h-full"
                color={tailwindColors.white.DEFAULT}
              />
            )}
          </div>
          <Badge
            size="medium"
            text={childName}
            color={tailwindColors.grey.light.DEFAULT}
            textColor={tailwindColors.grey.DEFAULT}
          />
        </InnerBox>

        <InnerBox className="rounded-lg p-4 bg-white-hover flex flex-col gap-3 flex-1">
          <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
            Informations sur le participant
          </Typography>
          <div className="grid grid-cols-2 gap-2 mobile:grid-cols-1">
            <LabelWithText label="Email" data={valueRender(enrollment.childEmail)} />
            <LabelWithText
              label="Numéro de téléphone"
              data={valueRender(enrollment.childPhoneNumber, (phone) => formatPhoneNumber(phone))}
            />
            <LabelWithText
              label="Niveau de la langue du pays choisi"
              data={valueRender(
                enrollment.chosenCountryLanguageLevel,
                (level) => languageLevelEnumMapping[level].label
              )}
            />
            <LabelWithText
              label="Autres langues étrangères étudiées ou parlées"
              data={
                enrollment.spokenLanguages?.length > 0 ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    {enrollment.spokenLanguages.map((lang, index) => (
                      <Typography key={index} variant="tag" color={tailwindColors.black.DEFAULT}>
                        {lang}
                        {index < enrollment.spokenLanguages.length - 1 && ','}
                      </Typography>
                    ))}
                  </div>
                ) : (
                  'Aucune langue renseignée'
                )
              }
            />

            {displayVersion === EnrollmentDisplayVersionEnum.LONG && (
              <LabelWithText
                label="Est-ce que le participant fume ?"
                data={valueRender(enrollment.doesChildSmoke, (val) => (val ? 'Oui' : 'Non'))}
              />
            )}
          </div>
        </InnerBox>

        <InnerBox className="rounded-lg p-4 bg-white-hover flex flex-col gap-3 flex-1">
          <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
            Caractère
          </Typography>
          {enrollment.characteristics?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {enrollment.characteristics.map((characteristic) => (
                <Badge
                  key={characteristic.id}
                  size="small"
                  variant="primary"
                  text={getCharacteristicName(characteristic)}
                />
              ))}
            </div>
          ) : (
            <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
              Aucune caractéristique renseignée
            </Typography>
          )}
        </InnerBox>

        <InnerBox className="rounded-lg p-4 bg-white-hover flex flex-col gap-3 flex-1">
          <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
            Loisirs, centres d'intérêt
          </Typography>
          {enrollment.hobbies?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {enrollment.hobbies.map((hobby) => (
                <Badge
                  key={hobby.id}
                  size="small"
                  variant="primary"
                  iconPosition="start"
                  icon={
                    hobby.picto ? (
                      <img
                        src={hobby.picto.path}
                        alt={hobby.name}
                        className="w-4 h-4 rounded-full"
                      />
                    ) : undefined
                  }
                  text={hobby.name}
                />
              ))}
            </div>
          ) : (
            <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
              Aucune caractéristique renseignée
            </Typography>
          )}
        </InnerBox>
      </div>
      <div className="w-full flex flex-col gap-3">
        <InnerBox className="rounded-lg p-4 bg-white-hover flex flex-col gap-3 flex-1">
          <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
            Famille
          </Typography>
          <div className="flex gap-9 mobile:flex-col mobile:gap-2">
            <LabelWithText
              label="Situation de famille des parents"
              data={valueRender(enrollment.familySituation, (val) =>
                t(maritalStatusEnumMapping[val])
              )}
            />
            <LabelWithText
              label="Numéro de sécurité sociale du parent"
              data={valueRender(enrollment.parentSocialSecurityNumber)}
            />
          </div>
          <div className="flex flex-col gap-2 ">
            <Typography variant="tag" color={tailwindColors.primary.DEFAULT}>
              Frères et soeurs
            </Typography>
            <DataTable<EnrollmentSiblingBasicType>
              manualSorting={false}
              columns={columns}
              bodyVariant="white"
              data={siblings.data}
              rowCount={siblings.data.length}
              paginated={false}
            />
          </div>
        </InnerBox>
        <InnerBox className="rounded-lg p-4 bg-white-hover flex flex-col gap-3 flex-1">
          <Typography variant="text" color={tailwindColors.grey.DEFAULT}>
            Autres informations
          </Typography>
          <LabelWithText
            label={
              <div className="flex items-center gap-1 mobile:flex-col mobile:gap-0 mobile:items-start">
                <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
                  Avez-vous en main la
                </Typography>
                <Typography.Link
                  variant="label-semibold"
                  color={tailwindColors.secondaryRed.DEFAULT}
                  className="underline"
                  onClick={() =>
                    window.open('https://sejour-linguistique-lec.fr/pdf/LEC-2026.pdf', '_blank')
                  }
                >
                  brochure LEC Séjours Linguistiques ?
                </Typography.Link>
              </div>
            }
            data={valueRender(enrollment.holdingLECBrochure, (val) => (val ? 'Oui' : 'Non'))}
          />
          <LabelWithText
            label="Comment l'avez-vous obtenue ?"
            data={valueRender(enrollment.howDidYouObtainLECBrochure?.name)}
          />
          <LabelWithText
            label="Est-ce le premier séjour linguistique de votre enfant (pour une durée de plus de 7 jours) ?"
            data={valueRender(enrollment.isChildFirstLanguageStay, (val) => (val ? 'Oui' : 'Non'))}
          />
          <LabelWithText
            label="Avec quel organisme votre enfant est-il parti ?"
            data={valueRender(enrollment.childPreviousTravelOrganization)}
          />
        </InnerBox>
      </div>
    </div>
  )
}

export default ChildBasicData
